import React, { useState, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { kpiAdminService } from '../../services/kpiAdminService';
import { kpiService } from '../../services/kpiService';
import { adminService } from '../../services/adminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { submissionService } from '../../services/submissionService';
import { slugifyKpiCode, dedupeKpiCode } from '../../utils/kpiCode';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { normalizeText } from '../../utils/normalizeInput';
import { KPISystemInfoTab } from './KPISystemInfoTab';
import { KPISystemDefinitionsTab } from './KPISystemDefinitionsTab';
import { KPISystemRulesTab } from './KPISystemRulesTab';

export function KPISystemEditModal({ system, isOpen, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('info');
  const [pendingDeleteDefId, setPendingDeleteDefId] = useState(null);
  const [discardInfoOpen, setDiscardInfoOpen] = useState(false);
  // Snapshot the info-tab values at mount so we can detect dirty state.
  // (KPISystems passes key={editingSystem?.id} so this component remounts
  // per system — the snapshot is always fresh.)
  const initialSystemDataRef = useRef({
    name: system?.name || '',
    country: system?.country || '',
    description: system?.description || '',
    version: system?.version || '',
    enabled_periods: system?.enabled_periods || [],
  });
  const [systemData, setSystemData] = useState(initialSystemDataRef.current);
  const [definitions, setDefinitions] = useState([]);
  const [showAddDefinitionForm, setShowAddDefinitionForm] = useState(false);
  const [newDefinition, setNewDefinition] = useState({ name: '', description: '', kpi_type: '' });
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [scoringRules, setScoringRules] = useState({});
  const [editingRules, setEditingRules] = useState(null);
  const [countries, setCountries] = useState([]);
  const [periodCounts, setPeriodCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadDefinitions = useCallback(async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    if (!system?.id) return;
    try {
      setLoading(true);
      const defs = await kpiAdminService.getKPIDefinitions(system.id);
      if (isCancelled()) return;
      setDefinitions(defs);

      // Single query for all rules in this system, then group client-side
      // (was: one getScoringRules per definition — N+1).
      const allRules = await kpiService.getScoringRules(system.id);
      if (isCancelled()) return;
      const rulesMap = defs.reduce((acc, def) => {
        acc[def.id] = allRules.filter((r) => r.kpi_definition_id === def.id);
        return acc;
      }, {});
      setScoringRules(rulesMap);
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [system?.id]);

  useAbortableFetch(
    ({ isCancelled }) => {
      if (isOpen && system?.id) loadDefinitions({ isCancelled });
    },
    [isOpen, system?.id, loadDefinitions]
  );

  useAbortableFetch(async ({ isCancelled }) => {
    if (!isOpen) return;
    const data = await adminService.getAllCountries();
    if (!isCancelled()) setCountries(data);
  }, [isOpen]);

  useAbortableFetch(async ({ isCancelled }) => {
    if (!isOpen || !system?.id) return;
    const counts = await submissionService.getPeriodCountsForSystem('kpi', system.id);
    if (!isCancelled()) setPeriodCounts(counts);
  }, [isOpen, system?.id]);

  // If the system has a legacy free-text country that isn't in the lookup
  // table, surface it as an extra option so editing doesn't silently strip
  // the value when the admin saves without changing it.
  const countryOptions = useMemo(() => {
    const names = countries.map((c) => c.name);
    const current = systemData.country;
    if (current && !names.includes(current)) {
      return [{ id: 'legacy', name: current, isLegacy: true }, ...countries];
    }
    return countries;
  }, [countries, systemData.country]);

  const previewedKpiCode = useMemo(() => {
    const base = slugifyKpiCode(newDefinition.name);
    return dedupeKpiCode(base, definitions.map((d) => d.kpi_code));
  }, [newDefinition.name, definitions]);

  const handleAddDefinition = async () => {
    if (!newDefinition.name.trim()) {
      setError('KPI Name is required');
      return;
    }
    if (!previewedKpiCode) {
      setError('KPI Name must contain letters or numbers (the generated code would be empty).');
      return;
    }
    if (!newDefinition.kpi_type) {
      setError('KPI Type is required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const created = await kpiAdminService.createKPIDefinition({
        kpi_system_id: system.id,
        kpi_code: previewedKpiCode,
        kpi_name: normalizeText(newDefinition.name),
        description: newDefinition.description,
        kpi_type: newDefinition.kpi_type,
      });
      setDefinitions([...definitions, created]);
      setNewDefinition({ name: '', description: '', kpi_type: '' });
      setShowAddDefinitionForm(false);
      toast.success('KPI definition added');
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDefinition = async (definitionId) => {
    if (!editingDefinition.kpi_name.trim()) {
      setError('KPI Name is required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await kpiAdminService.updateKPIDefinition(definitionId, {
        kpi_name: editingDefinition.kpi_name,
        description: editingDefinition.description,
      });
      setDefinitions(definitions.map((d) => (d.id === definitionId ? editingDefinition : d)));
      setEditingDefinition(null);
      toast.success('KPI definition updated');
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDefinition = (definitionId) => {
    setPendingDeleteDefId(definitionId);
  };

  const confirmDeleteDefinition = async () => {
    const definitionId = pendingDeleteDefId;
    setPendingDeleteDefId(null);
    if (!definitionId) return;
    try {
      setLoading(true);
      setError('');
      const rules = await kpiAdminService.getScoringRules(definitionId);
      for (const rule of rules) {
        await kpiAdminService.deleteScoringRule(rule.id);
      }
      await kpiAdminService.deleteKPIDefinition(definitionId);
      setDefinitions(definitions.filter((d) => d.id !== definitionId));
      const updatedRules = { ...scoringRules };
      delete updatedRules[definitionId];
      setScoringRules(updatedRules);
      toast.success('KPI definition deleted');
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async (ruleId) => {
    // editingRules stores target_value / points_for_achievement as the raw
    // string the user typed (so clearing the field doesn't immediately
    // produce NaN). Parse and validate at submit time.
    const target = parseFloat(editingRules.target_value);
    const points = parseFloat(editingRules.points_for_achievement);
    if (Number.isNaN(target) || target < 0) {
      setError('Target value must be a non-negative number.');
      return;
    }
    if (Number.isNaN(points) || points < 0) {
      setError('Points must be a non-negative number.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (ruleId) {
        await kpiAdminService.updateScoringRule(ruleId, {
          target_value: target,
          points_for_achievement: points,
        });
      } else {
        const created = await kpiAdminService.createScoringRule({
          kpi_definition_id: editingRules.kpi_definition_id,
          period: editingRules.period,
          target_value: target,
          points_for_achievement: points,
        });
        const defId = editingRules.kpi_definition_id;
        setScoringRules({
          ...scoringRules,
          [defId]: [...(scoringRules[defId] || []), created],
        });
      }
      setEditingRules(null);
      toast.success('Scoring rule saved');
      await loadDefinitions();
    } catch (err) {
      setError(mapSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const periodsKey = (arr) => [...(arr || [])].sort().join(',');
  const isInfoDirty =
    ['name', 'country', 'description', 'version'].some(
      (k) => (systemData[k] || '') !== (initialSystemDataRef.current[k] || '')
    ) || periodsKey(systemData.enabled_periods) !== periodsKey(initialSystemDataRef.current.enabled_periods);

  const handleClose = () => {
    if (isInfoDirty && !loading) setDiscardInfoOpen(true);
    else onClose();
  };

  const handleCancelAddDefinition = () => {
    setShowAddDefinitionForm(false);
    setNewDefinition({ name: '', description: '', kpi_type: '' });
    setError('');
  };

  if (!isOpen || !system) return null;

  return (
    <Modal onClose={handleClose}>
      <div className="kpi-system-edit-modal">
        <h2>Edit KPI System: {system.name}</h2>

        <div className="modal-tabs" role="tablist" aria-label="KPI system sections">
          {[
            { id: 'info', label: 'System Info' },
            { id: 'definitions', label: 'KPI Definitions' },
            { id: 'rules', label: 'Scoring Rules' },
          ].map((t) => (
            <button
              key={t.id}
              id={`system-tab-${t.id}`}
              role="tab"
              type="button"
              aria-selected={activeTab === t.id}
              aria-controls={`system-panel-${t.id}`}
              className={`modal-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          className="modal-tab-content"
          role="tabpanel"
          id={`system-panel-${activeTab}`}
          aria-labelledby={`system-tab-${activeTab}`}
        >
          {activeTab === 'info' && (
            <KPISystemInfoTab
              systemData={systemData}
              setSystemData={setSystemData}
              countryOptions={countryOptions}
              loading={loading}
              onSave={onSave}
              onGoToDefinitions={() => setActiveTab('definitions')}
              onCancel={handleClose}
              periodCounts={periodCounts}
            />
          )}
          {activeTab === 'definitions' && (
            <KPISystemDefinitionsTab
              definitions={definitions}
              showAddDefinitionForm={showAddDefinitionForm}
              setShowAddDefinitionForm={setShowAddDefinitionForm}
              newDefinition={newDefinition}
              setNewDefinition={setNewDefinition}
              previewedKpiCode={previewedKpiCode}
              editingDefinition={editingDefinition}
              setEditingDefinition={setEditingDefinition}
              onAddDefinition={handleAddDefinition}
              onUpdateDefinition={handleUpdateDefinition}
              onDeleteDefinition={handleDeleteDefinition}
              onCancelAdd={handleCancelAddDefinition}
              loading={loading}
              error={error}
            />
          )}
          {activeTab === 'rules' && (
            <KPISystemRulesTab
              definitions={definitions}
              scoringRules={scoringRules}
              editingRules={editingRules}
              setEditingRules={setEditingRules}
              onSaveRule={handleSaveRule}
              loading={loading}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={pendingDeleteDefId !== null}
        title="Delete this KPI Definition?"
        message="This permanently removes the KPI definition and cascades to all its scoring rules. Cannot be undone."
        confirmText="Delete"
        destructive
        onConfirm={confirmDeleteDefinition}
        onCancel={() => setPendingDeleteDefId(null)}
      />

      <ConfirmDialog
        isOpen={discardInfoOpen}
        title="Discard unsaved info-tab changes?"
        message="Definitions and scoring rules are saved immediately, but System Info edits aren't. Closing now will lose them."
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setDiscardInfoOpen(false); onClose(); }}
        onCancel={() => setDiscardInfoOpen(false)}
      />
    </Modal>
  );
}
